import React, { useEffect, useState } from 'react';
import '../styles/MainContainer.css';
import './Locations.css';
import api from '../wrapper/API';
import Popup from '../popup/Popup';
import ActionButton from '../input/ActionButton';
import OfflineBanner from '../shell/OfflineBanner';
import { usePopup } from '../context/PopupContext';
import { useSuccessBar } from '../context/SuccessBarContext';
import { useOffline } from '../context/OfflineContext';
import useLocations from '../../hooks/useLocations';
import LazyLocationMapDialog from '../location/LazyLocationMapDialog';

// inUse: the API refused the deletion although no event uses the location (a process, a textblock variant
// or a flat-rate/deduction deviation still references it) — set locally after a 409, the event count does
// not show that.
const usageLabel = (count, inUse = false) => {
  const n = Number(count ?? 0);
  if (n === 0) return inUse ? 'In Verwendung' : 'Nicht genutzt';
  return n === 1 ? '1 Spieltag' : `${n} Spieltage`;
};

function deleteErrorMessage(err) {
  const data = err?.response?.data;
  if (typeof data === 'string' && data.trim()) return data;
  if (!err?.response) return 'Keine Verbindung – die Location wurde nicht gelöscht.';
  if (err.response.status === 404) return 'Die Location existiert nicht mehr.';
  if (err.response.status === 409) {
    return 'Die Location wird noch verwendet – von Spieltagen, Prozessen, Textbaustein-Varianten oder Abweichungen bei Pauschalen/Abzügen – und kann nicht gelöscht werden.';
  }
  return 'Löschen fehlgeschlagen.';
}

/**
 * Admin page /locations (quick-access tile "Locations" under "Verwaltung" on the home page): all locations with
 * usage count; create/edit via the map dialog, delete only for unused ones (the API refuses locations
 * that still have events, processes, textblock variants or flat-rate/deduction deviations and says why).
 * Loads /Location/GetAll once; after saves/deletes the list is patched locally.
 * The event forms only offer selection + "Neue Location …" (components/location/LocationSelect).
 */
function Locations() {
  const { locations, loaded, loading, failed, ensureLoaded, upsert, remove } = useLocations();
  const { isOffline, onlineEpoch } = useOffline();
  const { openPopup, closePopup } = usePopup();
  const { startSuccessBarLoading, startSuccessBarSuccess, closeSuccessBar } = useSuccessBar();

  // `open` is kept separate from mode/location so the dialog keeps its title during the exit animation.
  const [dialog, setDialog] = useState({ open: false, mode: 'create', location: null });
  const [dialogEverOpened, setDialogEverOpened] = useState(false);
  const [pendingDelete, setPendingDelete] = useState(null);
  const [deleteError, setDeleteError] = useState('');

  // onlineEpoch: retry after "Erneut verbinden" if the first load failed (ensureLoaded is a no-op once loaded).
  useEffect(() => {
    if (!isOffline) ensureLoaded();
  }, [ensureLoaded, isOffline, onlineEpoch]);

  const openDialog = (mode, location = null) => {
    setDialog({ open: true, mode, location });
    setDialogEverOpened(true);
  };

  const closeDialog = () => setDialog(prev => ({ ...prev, open: false }));

  const handleSaved = (location) => {
    upsert(location);
    closeDialog();
  };

  const askDelete = (location) => {
    setPendingDelete(location);
    openPopup('confirmdeletelocation', location.id);
  };

  const handleConfirmDelete = () => {
    const location = pendingDelete;
    closePopup();
    if (!location) return;
    startSuccessBarLoading('Location wird gelöscht');
    api.delete(`/Location/Delete/${location.id}`)
      .then(() => {
        remove(location.id);
        startSuccessBarSuccess('Location gelöscht');
      })
      .catch(err => {
        closeSuccessBar();
        // Changed meanwhile (e.g. by another admin): bring the row in line with the server.
        const status = err?.response?.status;
        if (status === 404) remove(location.id);
        // Still referenced (events, processes, textblock variants or deviations — the message says which): the
        // row no longer offers deleting. The event count stays as loaded; a refused location without
        // events is marked "In Verwendung".
        if (status === 409) upsert({ ...location, deleteRefused: true });
        setDeleteError(deleteErrorMessage(err));
        openPopup('locationdeleteerror', location.id);
      });
  };

  const subtitle = !loaded
    ? (loading ? 'Lädt …' : '')
    : locations.length === 0
      ? 'Noch keine Locations angelegt'
      : `${locations.length} ${locations.length === 1 ? 'Location' : 'Locations'} · Löschen nur bei ungenutzten`;

  let content;
  if (isOffline && !loaded) {
    content = <p className="locationsMessage">Offline nicht verfügbar.</p>;
  } else if (!loaded) {
    // `failed` instead of !loading: before the first request starts, loading is still false.
    content = failed ? <p className="locationsMessage">Locations konnten nicht geladen werden.</p> : null;
  } else if (locations.length === 0) {
    content = <p className="locationsMessage">Lege die erste Location über „Neue Location“ an.</p>;
  } else {
    content = (
      <ul className="locationsList">
        {locations.map(l => {
          const unused = Number(l.eventCount ?? 0) === 0 && !l.deleteRefused;
          return (
            <li key={l.id} className="locationsRow">
              <span className="material-symbols-rounded locationsIcon" aria-hidden="true">location_on</span>
              <span className="locationsText">
                <span className="locationsName">{l.name}</span>
                {l.address && <span className="locationsAddress">{l.address}</span>}
              </span>
              <span className={`locationsUsage${unused ? ' isUnused' : ''}`}>{usageLabel(l.eventCount, l.deleteRefused)}</span>
              <span className="locationsActions">
                <button
                  type="button"
                  className="locationsBtn"
                  onClick={() => openDialog('edit', l)}
                  disabled={isOffline}
                  aria-label={`${l.name} bearbeiten`}
                  title={isOffline ? 'Offline nicht verfügbar' : 'Bearbeiten'}
                >
                  <span className="material-symbols-rounded" aria-hidden="true">edit</span>
                </button>
                {unused && (
                  <button
                    type="button"
                    className="locationsBtn isDanger"
                    onClick={() => askDelete(l)}
                    disabled={isOffline}
                    aria-label={`${l.name} löschen`}
                    title={isOffline ? 'Offline nicht verfügbar' : 'Löschen (nur ungenutzte Locations)'}
                  >
                    <span className="material-symbols-rounded" aria-hidden="true">delete</span>
                  </button>
                )}
              </span>
            </li>
          );
        })}
      </ul>
    );
  }

  return (
    <React.Fragment>
      <main className="defaultMain">
        <OfflineBanner />
        <div className="locationsHeader">
          <div className="locationsHeaderText">
            <h2 className="locationsTitle">Locations</h2>
            {subtitle && <span className="locationsSubtitle">{subtitle}</span>}
          </div>
          <ActionButton
            icon="add_location_alt"
            label="Neue Location"
            type={3}
            onClick={() => openDialog('create')}
            disabled={isOffline}
          />
        </div>
        {content}
      </main>

      {dialogEverOpened && (
        <LazyLocationMapDialog
          open={dialog.open}
          mode={dialog.mode}
          initial={dialog.location}
          fallbackCenter={dialog.location ?? locations[0] ?? null}
          existingLocations={locations}
          onClose={closeDialog}
          onSaved={handleSaved}
        />
      )}

      <Popup
        name="confirmdeletelocation"
        headerText="Location löschen"
        confirmText="Löschen"
        type="confirmaction"
        onConfirm={handleConfirmDelete}
        withCancel
      >
        <p>Soll die Location <b>{pendingDelete?.name}</b> dauerhaft gelöscht werden?</p>
        <p className="formFieldHint">
          Nicht möglich, solange ein Prozess, eine Textbaustein-Variante oder eine Abweichung bei
          Pauschalen/Abzügen sie noch verwendet.
        </p>
      </Popup>
      <Popup name="locationdeleteerror" headerText="Löschen fehlgeschlagen" confirmText="Ok" type="error">
        <p>{deleteError}</p>
      </Popup>
    </React.Fragment>
  );
}

export default Locations;
